import { useState, useMemo } from "react";
import { RefreshCw, Trash2, Inbox, ChevronRight, Search, X, MailOpen, Mail, ArrowUpDown, Clock, User, FileText, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/lib/i18n";
import { decodeMimeWords, senderLabel } from "@/lib/mime";

function sortMessages(messages, sortKey) {
  const sorted = [...messages];
  switch (sortKey) {
    case "date-asc":
      return sorted.sort((a, b) => new Date(a.received_at) - new Date(b.received_at));
    case "sender":
      return sorted.sort((a, b) => (a.sender || "").localeCompare(b.sender || ""));
    case "subject":
      return sorted.sort((a, b) => (a.subject || "").localeCompare(b.subject || ""));
    case "date-desc":
    default:
      return sorted.sort((a, b) => new Date(b.received_at) - new Date(a.received_at));
  }
}

export default function InboxSection({
  email, messages, selectedMessages, isDeletingMessages, isRefreshingInbox,
  refreshInbox, selectAllMessages, toggleMessageSelection, deleteSelectedMessages,
  markSelectedMessages, fetchMessage, formatDate,
}) {
  const { t } = useI18n();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState("date-desc");

  const sortOptions = [
    { key: "date-desc", label: "Date ↓", icon: Clock },
    { key: "date-asc", label: "Date ↑", icon: Clock },
    { key: "sender", label: t.sender, icon: User },
    { key: "subject", label: t.subject, icon: FileText },
  ];

  const filteredMessages = useMemo(() => {
    let result = messages;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (m) => m.sender?.toLowerCase().includes(q) || m.subject?.toLowerCase().includes(q)
      );
    }
    return sortMessages(result, sortKey);
  }, [messages, searchQuery, sortKey]);
  return (
    <div className="col-span-12">
      <div className="border border-zinc-800 bg-zinc-950/80">
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Inbox className="w-4 h-4 text-cyan-400" />
            <span className="font-mono text-xs sm:text-sm uppercase tracking-widest text-zinc-400">
              {t.inbox}
            </span>
            <Badge
              className="ml-2 bg-cyan-500/10 text-cyan-400 border-cyan-500/30 font-mono"
              data-testid="message-count"
            >
              {messages.length}
            </Badge>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            {selectedMessages.length > 0 ? (
              <>
                <Button
                  onClick={() => markSelectedMessages(true)}
                  variant="ghost"
                  size="sm"
                  className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 font-mono text-xs"
                  title={t.markRead}
                >
                  <MailOpen className="w-3 h-3 sm:mr-1" />
                  <span className="hidden sm:inline">{t.read}</span>
                </Button>
                <Button
                  onClick={() => markSelectedMessages(false)}
                  variant="ghost"
                  size="sm"
                  className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 font-mono text-xs"
                  title={t.markUnread}
                >
                  <Mail className="w-3 h-3 sm:mr-1" />
                  <span className="hidden sm:inline">{t.unread}</span>
                </Button>
                <Button
                  onClick={deleteSelectedMessages}
                  disabled={isDeletingMessages}
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-400 hover:bg-red-500/10 font-mono text-xs"
                  data-testid="delete-selected-button"
                >
                  <Trash2 className={`w-3 h-3 sm:mr-1 ${isDeletingMessages ? "animate-pulse" : ""}`} />
                  <span className="hidden sm:inline">{t.deleteCount(selectedMessages.length)}</span>
                  <span className="sm:hidden">({selectedMessages.length})</span>
                </Button>
              </>
            ) : (
              email && (
                <Button
                  onClick={refreshInbox}
                  disabled={isRefreshingInbox}
                  variant="ghost"
                  size="sm"
                  className="text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 font-mono text-xs group/refresh"
                  data-testid="refresh-inbox-button"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 transition-transform duration-500 ${isRefreshingInbox ? "animate-spin" : "group-hover/refresh:rotate-[360deg]"}`} />
                  {isRefreshingInbox ? "..." : t.refresh}
                </Button>
              )
            )}
          </div>
        </div>

        {/* Search bar + Sort */}
        {messages.length > 1 && (
          <div className="px-3 sm:px-4 py-2 border-b border-zinc-800/50 bg-zinc-900/30 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="pl-9 pr-8 bg-zinc-900 border-zinc-700/50 text-white text-xs h-8 font-mono"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto">
              <ArrowUpDown className="w-3 h-3 text-zinc-600 shrink-0" />
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortKey(opt.key)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono whitespace-nowrap transition-colors ${
                    sortKey === opt.key
                      ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                      : "text-zinc-500 hover:text-cyan-400 hover:bg-cyan-500/10 border border-zinc-800 hover:border-cyan-500/30"
                  }`}
                >
                  <opt.icon className="w-2.5 h-2.5" />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Select all checkbox */}
        {filteredMessages.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-2 border-b border-zinc-800/50 bg-zinc-900/30">
            <Checkbox
              id="select-all"
              checked={selectedMessages.length === messages.length && messages.length > 0}
              onCheckedChange={selectAllMessages}
              className="border-zinc-600 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
              data-testid="select-all-checkbox"
            />
            <label
              htmlFor="select-all"
              className="text-xs font-mono text-zinc-500 cursor-pointer"
            >
              {selectedMessages.length === messages.length ? t.deselectAll : t.selectAll}
            </label>
          </div>
        )}

        <ScrollArea className="h-[400px]">
          {filteredMessages.length > 0 ? (
            <div className="divide-y divide-zinc-800/50">
              {filteredMessages.map((message, index) => (
                <div
                  key={message.id}
                  className={`message-item p-4 border-l-2 ${
                    message.direction === "sent"
                      ? "border-emerald-500/50 bg-emerald-500/5"
                      : message.is_read
                        ? "border-transparent"
                        : "border-cyan-500 bg-cyan-500/5"
                  } ${selectedMessages.includes(message.id) ? "bg-cyan-500/10" : ""} animate-fade-in-up opacity-0`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`message-item-${message.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedMessages.includes(message.id)}
                        onCheckedChange={() => toggleMessageSelection(message.id)}
                        className="border-zinc-600 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                        data-testid={`message-checkbox-${message.id}`}
                      />
                    </div>

                    <div
                      className="flex-1 cursor-pointer"
                      onClick={() => fetchMessage(message.id)}
                    >
                      <div className="flex items-start justify-between gap-2 sm:gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {message.direction === "sent" ? (
                              <Send className="w-3 h-3 text-emerald-400 shrink-0" />
                            ) : !message.is_read ? (
                              <div className="w-2 h-2 rounded-full bg-cyan-400 pulse-new shrink-0"></div>
                            ) : null}
                            <span className={`font-mono text-xs sm:text-sm truncate min-w-0 ${message.direction === "sent" ? "text-emerald-400" : "text-white"}`}>
                              {message.direction === "sent" ? `→ ${message.recipient || message.sender}` : senderLabel(message)}
                            </span>
                            {message.direction === "sent" && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 font-mono shrink-0">Envoyé</span>
                            )}
                          </div>
                          <p className="text-zinc-300 truncate font-medium text-sm sm:text-base">
                            {decodeMimeWords(message.subject)}
                          </p>
                          <span className="font-mono text-[10px] sm:hidden text-zinc-600 mt-1 block">
                            {formatDate(message.received_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-500 shrink-0">
                          <span className="font-mono text-xs hidden sm:inline">
                            {formatDate(message.received_at)}
                          </span>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : searchQuery ? (
            <div className="flex flex-col items-center justify-center h-full py-16 text-zinc-600">
              <Search className="w-10 h-10 text-zinc-700 mb-4" />
              <p className="font-mono text-sm">
                {t.noResults(searchQuery)}
              </p>
              <button
                onClick={() => setSearchQuery("")}
                className="text-xs mt-2 text-cyan-500 hover:text-cyan-400 font-mono"
              >
                {t.clearSearch}
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-16 text-zinc-600">
              <div className="float-animation mb-4">
                <svg
                  className="w-16 h-16 text-zinc-700"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <p className="font-mono text-sm uppercase tracking-widest">
                {t.waitingTransmission}
              </p>
              <p className="text-xs mt-2 text-zinc-700">
                {t.messagesWillAppear}
              </p>
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
